import { JSX } from 'react';
import DataUrlHelper from '../../../../helpers/data-url.helper';
import { LanguageService } from '../../../../services/language.service';

// The svg imports in ALPHABETICAL ORDER
import alternativesDefaultGray900 from './icon-files/alternatives-default-gray-900.svg';
import bikeDefaultGray900 from './icon-files/bike-default-gray-900.svg';
import busDefaultGray900 from './icon-files/bus-default-gray-900.svg';
import bussOnDemandDefaultGray900 from './icon-files/buss-on-demand-default-gray-900.svg';
import calendarDefaultGray900 from './icon-files/calendar-default-gray-900.svg';
import calendarWithArrowDefaultGray900 from './icon-files/calendar-with-arrow-default-gray-900.svg';
import carDefaultGray900 from './icon-files/car-default-gray-900.svg';
import exclamationCircleGray900 from './icon-files/exclamation-circle-gray-900.svg';
import exclamationHexagonFilledRed02 from './icon-files/exclamation-hexagon-filled-red-600.svg';
import exclamationTriangleFilledOrange500 from './icon-files/exclamation-triangle-filled-orange-500.svg';
import ferryDefaultGray900 from './icon-files/ferry-default-gray-900.svg';
import infoCircleFilledBlue800 from './icon-files/info-circle-filled-blue-800.svg';
import taxiDefaultGray900 from './icon-files/taxi-default-gray-900.svg';
import trainDefaultGray900 from './icon-files/train-default-gray-900.svg';
import tramDefaultGray900 from './icon-files/tram-default-gray-900.svg';
import walkDefaultGray900 from './icon-files/walk-default-gray-900.svg';

import { IconMotif } from './icon-motif.enum';
import { IconColor } from './icon-color.enum';
import { IconVariant } from './icon-variant.enum';
import { IconDefinition } from './icon-definition.type';

// === ICON DEFINITIONS ===
// The icon definitions in ALPHABETICAL ORDER
// prettier-ignore
const iconDefinitions: IconDefinition[] = [
  { motif: IconMotif.Alternatives, color: IconColor.Gray900, variant: IconVariant.Default, url: alternativesDefaultGray900, altTextSv: 'Alternativ', altTextEn: 'Alternatives' },
  { motif: IconMotif.Bike, color: IconColor.Gray900, variant: IconVariant.Default, url: bikeDefaultGray900, altTextSv: 'Cykel', altTextEn: 'Bike' },
  { motif: IconMotif.Bus, color: IconColor.Gray900, variant: IconVariant.Default, url: busDefaultGray900, altTextSv: 'Buss', altTextEn: 'Bus' },
  { motif: IconMotif.BussOnDemand, color: IconColor.Gray900, variant: IconVariant.Default, url: bussOnDemandDefaultGray900, altTextSv: 'Buss on demand', altTextEn: 'Bus on demand' },
  { motif: IconMotif.Calendar, color: IconColor.Gray900, variant: IconVariant.Default, url: calendarDefaultGray900, altTextSv: 'Kalender', altTextEn: 'Calendar' },
  { motif: IconMotif.CalendarWithArrow, color: IconColor.Gray900, variant: IconVariant.Default, url: calendarWithArrowDefaultGray900, altTextSv: 'Kalender med pil', altTextEn: 'Calendar with arrow' },
  { motif: IconMotif.Car, color: IconColor.Gray900, variant: IconVariant.Default, url: carDefaultGray900, altTextSv: 'Bil', altTextEn: 'Car' },
  { motif: IconMotif.Exclamation, color: IconColor.Gray900, variant: IconVariant.Circle, url: exclamationCircleGray900, altTextSv: 'Grått utropstecken', altTextEn: 'Gray exclamation mark' },
  { motif: IconMotif.Exclamation, color: IconColor.Orange500, variant: IconVariant.TriangleFilled, url: exclamationTriangleFilledOrange500, altTextSv: 'Orange utropstecken', altTextEn: 'Orange exclamation mark' },
  { motif: IconMotif.Exclamation, color: IconColor.Red600, variant: IconVariant.HexagonFilled, url: exclamationHexagonFilledRed02, altTextSv: 'Rött utropstecken', altTextEn: 'Red exclamation mark' },
  { motif: IconMotif.Ferry, color: IconColor.Gray900, variant: IconVariant.Default, url: ferryDefaultGray900, altTextSv: 'Färja', altTextEn: 'Ferry' },
  { motif: IconMotif.Info, color: IconColor.Blue800, variant: IconVariant.CircleFilled, url: infoCircleFilledBlue800, altTextSv: 'Blå informationssymbol', altTextEn: 'Blue information symbol' },
  { motif: IconMotif.Taxi, color: IconColor.Gray900, variant: IconVariant.Default, url: taxiDefaultGray900, altTextSv: 'Taxi', altTextEn: 'Taxi' },
  { motif: IconMotif.Train, color: IconColor.Gray900, variant: IconVariant.Default, url: trainDefaultGray900, altTextSv: 'Tåg', altTextEn: 'Train' },
  { motif: IconMotif.Tram, color: IconColor.Gray900, variant: IconVariant.Default, url: tramDefaultGray900, altTextSv: 'Spårvagn', altTextEn: 'Tram' },
  { motif: IconMotif.Walk, color: IconColor.Gray900, variant: IconVariant.Default, url: walkDefaultGray900, altTextSv: 'Gång', altTextEn: 'Walk' },
];

// === HELPER FUNCTIONS ===
function getIconDefinition(motif: IconMotif, color: IconColor, variant: IconVariant): IconDefinition | undefined {
  const def = iconDefinitions.find((icon) => icon.motif === motif && icon.color === color && icon.variant === variant);
  if (!def) {
    return undefined;
  }
  return def;
}

function getAltText(altText: string | undefined, def: IconDefinition): string | undefined {
  if (altText !== undefined) return altText;
  return LanguageService.getCurrentLanguage() === LanguageService.English ? def.altTextEn : def.altTextSv;
}

// === COMPONENT ===
export function Icon({
  motif,
  color = IconColor.Gray900,
  variant = IconVariant.Default,
  altText,
  title,
  className = '',
}: {
  motif: IconMotif;
  color?: IconColor;
  variant?: IconVariant;
  altText?: string | undefined;
  title?: string | undefined;
  className?: string;
}): JSX.Element | null {
  const def = getIconDefinition(motif, color, variant);
  if (!def) return null;
  return <img src={DataUrlHelper.encode(def.url)} alt={getAltText(altText, def)} title={title} className={className} />;
}
